import { useQuery } from '@tanstack/react-query';
import { apiClient, type MeResponse } from '@/lib/api-client';

/**
 * An app in the admin shell. Open string, not a closed union: apps are added over
 * time, and the backend already treats this as data (see RESOURCE_CATALOG).
 *
 * The known values are kept as constants so a typo still shows up at the call
 * sites that use them.
 */
export type PermissionResource = string;

export const REVIEWS: PermissionResource = 'reviews';
export const EVENTS: PermissionResource = 'events';

export interface Permissions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  // Veranstaltungen lifecycle actions (harmless for reviews — default false):
  canCreate: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canReject: boolean;
  canReopen: boolean;
}

const NO_ACCESS: Permissions = {
  canView: false,
  canEdit: false,
  canDelete: false,
  canCreate: false,
  canSubmit: false,
  canApprove: false,
  canReject: false,
  canReopen: false,
};

function toPermissions(actions: string[]): Permissions {
  return {
    canView: actions.includes('view'),
    canEdit: actions.includes('edit'),
    canDelete: actions.includes('delete'),
    canCreate: actions.includes('create'),
    canSubmit: actions.includes('submit'),
    canApprove: actions.includes('approve'),
    canReject: actions.includes('reject'),
    canReopen: actions.includes('reopen'),
  };
}

/** Shared query so all callers reuse a single /api/me request. */
export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => apiClient.getMe(),
    staleTime: 5 * 60_000,
    retry: false, // a 401 is an answer, not a transient failure
  });
}

/**
 * Permission hook, generalized over a resource. Defaults to 'reviews' so
 * existing callers (`usePermissions()`) keep their previous behavior.
 *
 * The action lists come from GET /api/me — the backend allowlist and role
 * matrix. They used to be evaluated in the browser from a bundled JSON file,
 * which was bypassable; the server is now the only authority. Handlers enforce
 * the same rules independently, so these flags only decide what the UI offers.
 *
 * Nothing is granted while the request is in flight, so no action briefly
 * appears before its permission is known.
 */
export function usePermissions(resource: PermissionResource = 'reviews'): Permissions {
  const { data } = useMe();

  if (!data) return NO_ACCESS;

  return toPermissions(data.permissions[resource] ?? []);
}
